import { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtom } from "jotai";

import {
    Button,
    Drawer as ChakraDrawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    FormControl,
    FormLabel
} from "@chakra-ui/react";

import { appAtom } from "@atoms/appAtom.ts";

import {
    CountryOption,
    CountryValueContainer,
    Select
} from "@components/Select";
import { Text } from "@components/Text";

interface PropTypes {
    isOpen: boolean;
    onClose: () => void;
}

interface LocLangFormData {
    location: string;
    language: string;
}

// Dummy list of countries
const countriesOptions = [
    { value: "af", label: "Afghanistan" },
    { value: "al", label: "Albania" },
    { value: "dz", label: "Algeria" },
    { value: "ar", label: "Argentina" },
    { value: "au", label: "Australia" },
    { value: "at", label: "Austria" },
    { value: "bd", label: "Bangladesh" },
    { value: "by", label: "Belarus" },
    { value: "be", label: "Belgium" },
    { value: "br", label: "Brazil" },
    { value: "bg", label: "Bulgaria" },
    { value: "ca", label: "Canada" },
    { value: "cl", label: "Chile" },
    { value: "cn", label: "China" },
    { value: "co", label: "Colombia" },
    { value: "hr", label: "Croatia" },
    { value: "cu", label: "Cuba" },
    { value: "cz", label: "Czech Republic" },
    { value: "dk", label: "Denmark" },
    { value: "eg", label: "Egypt" },
    { value: "fi", label: "Finland" },
    { value: "fr", label: "France" },
    { value: "de", label: "Germany" },
    { value: "gr", label: "Greece" },
    { value: "hk", label: "Hong Kong" },
    { value: "hu", label: "Hungary" },
    { value: "is", label: "Iceland" },
    { value: "in", label: "India" },
    { value: "id", label: "Indonesia" },
    { value: "ir", label: "Iran" },
    { value: "iq", label: "Iraq" },
    { value: "ie", label: "Ireland" },
    { value: "il", label: "Israel" },
    { value: "it", label: "Italy" },
    { value: "jp", label: "Japan" },
    { value: "jo", label: "Jordan" },
    { value: "ke", label: "Kenya" },
    { value: "kr", label: "South Korea" },
    { value: "kw", label: "Kuwait" },
    { value: "lb", label: "Lebanon" },
    { value: "my", label: "Malaysia" },
    { value: "mx", label: "Mexico" },
    { value: "ma", label: "Morocco" },
    { value: "nl", label: "Netherlands" },
    { value: "nz", label: "New Zealand" },
    { value: "ng", label: "Nigeria" },
    { value: "no", label: "Norway" },
    { value: "pk", label: "Pakistan" },
    { value: "pe", label: "Peru" },
    { value: "ph", label: "Philippines" },
    { value: "pl", label: "Poland" },
    { value: "pt", label: "Portugal" },
    { value: "qa", label: "Qatar" },
    { value: "ro", label: "Romania" },
    { value: "ru", label: "Russia" },
    { value: "sa", label: "Saudi Arabia" },
    { value: "rs", label: "Serbia" },
    { value: "sg", label: "Singapore" },
    { value: "za", label: "South Africa" },
    { value: "es", label: "Spain" },
    { value: "lk", label: "Sri Lanka" },
    { value: "se", label: "Sweden" },
    { value: "ch", label: "Switzerland" },
    { value: "tw", label: "Taiwan" },
    { value: "tz", label: "Tanzania" },
    { value: "th", label: "Thailand" },
    { value: "tr", label: "Turkey" },
    { value: "ug", label: "Uganda" },
    { value: "ua", label: "Ukraine" },
    { value: "ae", label: "United Arab Emirates" },
    { value: "gb", label: "United Kingdom" },
    { value: "us", label: "United States" },
    { value: "vn", label: "Vietnam" },
    { value: "ye", label: "Yemen" }
];

// Dummy list of languages (used in translation)
const languagesOptions = [
    {
        value: "en",
        label: "English"
    }
];
const LocLangSelect = ({ isOpen, onClose }: PropTypes) => {
    const {
        i18n: { changeLanguage },
        t
    } = useTranslation();

    const DrawerRefBtn = useRef(null);

    const [app, setApp] = useAtom(appAtom);

    const { control, handleSubmit } = useForm<LocLangFormData>({
        defaultValues: { language: app.language, location: app.location }
    });

    const onSubmitLocLangForm = ({ language, location }: LocLangFormData) => {
        setApp(prev => ({ ...prev, location, language }));
        changeLanguage(language);
        onClose();
    };

    return (
        <ChakraDrawer
            isOpen={isOpen}
            placement="right"
            onClose={onClose}
            finalFocusRef={DrawerRefBtn}
            size={["full", null, "md"]}
            variant="primary"
        >
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton>
                    <FontAwesomeIcon
                        icon={faXmark}
                        color="var(--euka-colors-gray-900)"
                        fontSize={22}
                    />
                </DrawerCloseButton>
                <DrawerHeader mt={5}>
                    <Text type="subHeading">
                        {t(
                            "checkout.locLangSelectDrawer.title",
                            "Select location and language"
                        )}
                    </Text>
                </DrawerHeader>

                <DrawerBody mt={12}>
                    {/** Language Location Form */}
                    <Flex direction="column" as="form" gap={10}>
                        <Controller
                            name="location"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <FormControl>
                                    <FormLabel>
                                        {t(
                                            "checkout.locLangSelectDrawer.selectLocLabel",
                                            "Select your location"
                                        )}
                                    </FormLabel>
                                    <Select
                                        option={CountryOption}
                                        valueContainer={CountryValueContainer}
                                        value={countriesOptions.find(
                                            o => o.value === value
                                        )}
                                        onChange={v => onChange(v?.value)}
                                        options={countriesOptions}
                                        placeholder={t(
                                            "checkout.locLangSelectDrawer.selectLoc",
                                            "Select your location"
                                        )}
                                    />
                                </FormControl>
                            )}
                        />
                        <Controller
                            name="language"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <FormControl>
                                    <FormLabel>
                                        {t(
                                            "checkout.locLangSelectDrawer.selectLangLabel",
                                            "Select your language"
                                        )}
                                    </FormLabel>
                                    <Select
                                        value={languagesOptions.find(
                                            o => o.value === value
                                        )}
                                        onChange={v => onChange(v?.value)}
                                        options={languagesOptions}
                                        placeholder={t(
                                            "checkout.locLangSelectDrawer.selectLang",
                                            "Select your language"
                                        )}
                                    />
                                </FormControl>
                            )}
                        />
                    </Flex>
                </DrawerBody>

                <DrawerFooter>
                    <Button
                        mr="auto"
                        colorScheme="ebonyClay"
                        onClick={handleSubmit(onSubmitLocLangForm)}
                    >
                        {t(
                            "checkout.locLangSelectDrawer.confirmBtn",
                            "Confirm changes"
                        )}
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </ChakraDrawer>
    );
};

export default LocLangSelect;
